// src/helpers/createSphere.ts

import { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere } from 'types/global';

interface CreateSphereParams {
  schemaSystemName: string;
  sphere: Partial<Sphere>;
  /** Approach system names to deploy inline after the sphere vertex is created.
   *  The graph-service route deploys each via the in-process `deployApproach`
   *  helper using the same request context — failure of any approach aborts
   *  sphere creation (atomic). Use this instead of a separate POST
   *  /approaches/deploy call when the deployment should be bundled with sphere
   *  creation (e.g. `deploy_organization` bootstrap). */
  approaches?: string[];
  eventAdditionalData?: Record<string, unknown>;
}

export interface CreateSphereResponse {
  status: string;
  sphere: Sphere;
  /** Saga deployment IDs for approaches deployed inline with the sphere (one
   *  per `approaches[]` entry). Present only when `approaches` was supplied.
   *  Approaches finalize asynchronously — poll GET /approaches/deployments/:sphereId. */
  approachDeploymentIds?: string[];
}

export interface CreateSphereErrorUpstream {
  /** The HTTP status graph-service answered; absent when it gave no answer. */
  status?: number;
  /** graph-service's `error` (a stable code such as `sphere_write_unavailable` on a 503). */
  code?: string;
  /** graph-service's own `details`, unprefixed (POST /spheres sends an array of strings). */
  details?: unknown;
  /**
   * True when nothing refused the create: graph-service answered 503 or 429, a gateway in front of it
   * answered 502 or 504, or the request got no answer (connection refused, reset or timed out). The
   * caller answers 503 and the create can be retried. A 500 is graph-service's own fault, not this.
   * A create that timed out may still have committed, as graph-service's own `sphere_write_unavailable`
   * says for a vertex write with no answer.
   */
  unavailable: boolean;
  /**
   * The sphere graph-service created before the failure and left standing (its default arcs, an
   * approach or its override arcs did not deploy). Absent when nothing was created or it was rolled
   * back. A caller that treats the create as failed removes it, or its retry creates a second sphere.
   */
  sphereId?: string;
}

export class CreateSphereError extends Error {
  details: string | string[];
  /** What graph-service answered; absent for a request this helper refused to send. */
  upstream?: CreateSphereErrorUpstream;
  /** The HTTP status graph-service answered, when it answered (`upstream.status`); a 503 is retryable. */
  status?: number;
  /**
   * The `error` graph-service answered, unchanged (`upstream.code`): the stable code a caller decides on
   * (409 `sphere_id_in_other_partition` is not 409 `Sphere already exists`). Absent when graph-service
   * did not answer.
   */
  code?: string;

  constructor(message: string, details: string | string[], upstream?: CreateSphereErrorUpstream) {
    super(message);
    this.details = details;
    this.name = 'CreateSphereError';
    this.status = upstream?.status;
    this.code = upstream?.code;
    if (upstream) this.upstream = upstream;
  }
}

/**
 * The statuses that say graph-service could not take the create right now rather than refusing it: its
 * own 503 and 429, and a gateway's 502 (no healthy instance) and 504 (the instance did not answer in time).
 */
const UNAVAILABLE_STATUSES: ReadonlySet<number> = new Set([429, 502, 503, 504]);

/** What a failed POST /spheres said: its status and body, or that it got no answer. */
function upstreamOf(err: any): CreateSphereErrorUpstream | undefined {
  const status: unknown = err?.response?.status;
  if (typeof status === 'number') {
    const data = err.response.data;
    return {
      status,
      ...(typeof data?.error === 'string' ? { code: data.error } : {}),
      ...(data?.details !== undefined ? { details: data.details } : {}),
      unavailable: UNAVAILABLE_STATUSES.has(status),
      ...(typeof data?.sphereId === 'string' && data.sphereId ? { sphereId: data.sphereId } : {}),
    };
  }
  // An axios error with no response: the request was sent and nothing answered it.
  if (err?.isAxiosError === true) return { unavailable: true };
  return undefined;
}

/**
 * Calls POST /spheres to create a new sphere.
 *
 * @param apiClient        – an ApiClient configured for your Graph endpoint
 * @param params.schemaSystemName – the system_name of the schema to use
 * @param params.sphere    – the partial Sphere payload
 * @returns the created Sphere wrapped in a status envelope
 * @throws CreateSphereError on failure; its `upstream` says what graph-service answered, and
 *   `upstream.unavailable` that nothing refused the create (a 503, 429, 502 or 504, or no answer).
 */
export async function createSphere(
  apiClient: ApiClient,
  params: CreateSphereParams
): Promise<CreateSphereResponse> {
  const { schemaSystemName, sphere, approaches, eventAdditionalData } = params;

  if (!schemaSystemName?.trim() || !sphere) {
    throw new CreateSphereError(
      'Invalid parameters',
      'schemaSystemName and sphere are required'
    );
  }

  try {
    // ApiClient.post automatically handles the Bearer token header
    const body: Record<string, unknown> = { schemaSystemName, sphere };
    if (approaches && approaches.length > 0) body['approaches'] = approaches;
    if (eventAdditionalData) body['eventAdditionalData'] = eventAdditionalData;
    const data = await apiClient.post<CreateSphereResponse>(
      '/spheres',
      body
    );
    return data;
  } catch (err: any) {
    const errorMsg =
      err.response?.data?.error ??
      err.message ??
      'Unknown error creating sphere';
    const errorDetails = err.response?.data?.details;
    const msg = errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg;
    throw new CreateSphereError('Failed to create sphere.', msg, upstreamOf(err));
  }
}
